import { getLoadedVariants, loadAllVariants } from "./product-variants-loader";

class DynamicProductFormSelectors extends HTMLElement {
  constructor() {
    super();

    this.sectionData = null;
    this.variants = [];
    this.variantInput = null;
    this.cartQuantities = {};

    this.isHomeTryOn = false;
    this.productId = null;
    this.htoCartLimit = Infinity;
    this.cartQuantityForProduct = 0;

    this.handleChange = this.handleChange.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
  }

  connectedCallback() {
    this.sectionData = this.getSectionData();
    this.mtoLookups = this.getMtoLookups();
    this.variants = this.sectionData?.variants || [];
    this.variantsCount = Number(this.sectionData?.variantsCount) || this.variants.length;
    this.mergeVariants(getLoadedVariants(this.sectionData?.selectorProductId) || []);
    this.cartQuantities = this.sectionData?.cartQuantities || {};
    this.optionCount = this.sectionData?.optionCount || 0;
    this.isHomeTryOn = Boolean(this.sectionData?.isHomeTryOn);
    this.productId = this.sectionData?.productId != null ? String(this.sectionData.productId) : null;
    this.htoCartLimit = this.sectionData?.htoCartLimit ?? Infinity;
    this.cartQuantityForProduct = this.sectionData?.cartQuantityForProduct || 0;

    this.variantInput = this.querySelector("[data-selected-variant-id]");

    this.removeEventListener("change", this.handleChange);
    this.addEventListener("change", this.handleChange);

    document.removeEventListener("updateCart", this.handleCartUpdate);

    document.addEventListener("updateCart", this.handleCartUpdate);

    this.autoSelectSingleChoices();

    this.updateSelectedLabels();
    this.updateVariantAvailability();
    this.syncOptionProperties();
    this.updateStepSelectionState();
    this.updateSlitSwapLinks();

    const selectedVariant = this.findSelectedVariant();

    const urlVariantId = new URLSearchParams(window.location.search).get("variant");

    const hasUrlVariantSelection = Boolean(
      urlVariantId && selectedVariant && String(selectedVariant.id) === urlVariantId,
    );

    this.updateVariantHelperText(hasUrlVariantSelection ? selectedVariant : null);

    // Auto-selection can complete a variant the Liquid id input lacks; sync it without rewriting the URL.
    if (selectedVariant && this.variantInput && this.variantInput.value !== String(selectedVariant.id)) {
      this.variantInput.value = selectedVariant.id;
      this.setAttribute("current-variant", selectedVariant.id);

      const variantIdElement = this.querySelector(".product-form__variant-id");

      if (variantIdElement) {
        variantIdElement.dataset.variantid = selectedVariant.id;
      }

      this.updateVariantPrice(selectedVariant);
      this.dispatchVariantChange(selectedVariant);
    }

    this.refreshAddToCartState(selectedVariant);

    const selectedDeliveryInput = this.querySelector("[data-delivery-time-input]:checked");

    if (selectedDeliveryInput) {
      this.updateDeliveryEstimate(selectedDeliveryInput);
    }

    this.loadRemainingVariants();
  }

  // Liquid caps [data-section-data] at 250 variants, so values beyond it would look unavailable;
  // fetch the full list and merge in what's missing.
  get variantsIncomplete() {
    return this.variantsCount > this.variants.length;
  }

  async loadRemainingVariants() {
    const productId = this.sectionData?.selectorProductId;

    if (!productId || !this.variantsIncomplete) {
      return;
    }

    const fetchedVariants = await loadAllVariants(productId, this.sectionData?.selectorProductUrl || "");

    if (!this.isConnected || !fetchedVariants.length) {
      return;
    }

    const previouslySelectedVariant = this.findSelectedVariant();

    if (!this.mergeVariants(fetchedVariants)) {
      return;
    }

    const selectedVariant = this.findSelectedVariant();

    this.updateVariantAvailability();
    this.updateOptionSoldOutMessages(selectedVariant);

    if (selectedVariant && !previouslySelectedVariant) {
      this.setSelectedVariant(selectedVariant);
    }
  }

  // Adds the variants the Liquid JSON is missing; returns whether any were added.
  mergeVariants(fetchedVariants) {
    const knownIds = new Set(this.variants.map((variant) => String(variant.id)));
    const missingVariants = fetchedVariants
      .filter((variant) => !knownIds.has(String(variant.id)))
      .map((variant) => ({
        ...variant,
        featuredMediaId: this.findClosestVariantMediaId(variant),
      }));

    if (!missingVariants.length) {
      return false;
    }

    this.variants = [...this.variants, ...missingVariants];
    this.variantsCount = Math.max(this.variantsCount, this.variants.length);

    return true;
  }

  // Storefront API has no featured media id, so borrow it from the Liquid variant sharing the most options.
  findClosestVariantMediaId(variant) {
    let bestMatch = null;
    let bestScore = -1;

    this.variants.forEach((candidate) => {
      const score = candidate.options.filter((option) =>
        variant.options.some(
          (variantOption) => variantOption.position === Number(option.position) && variantOption.name === option.name,
        ),
      ).length;

      if (score > bestScore) {
        bestScore = score;
        bestMatch = candidate;
      }
    });

    return bestMatch?.featuredMediaId ?? null;
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.handleChange);

    document.removeEventListener("updateCart", this.handleCartUpdate);
  }

  buildCartQuantities(items) {
    return items.reduce((quantities, item) => {
      const variantId = String(item.variant_id);

      quantities[variantId] = (quantities[variantId] || 0) + item.quantity;

      return quantities;
    }, {});
  }

  buildCartQuantityForProduct(items) {
    if (!this.productId) {
      return 0;
    }

    return items.reduce(
      (total, item) => (String(item.product_id) === this.productId ? total + item.quantity : total),
      0,
    );
  }

  handleCartUpdate(event) {
    const items = event.detail?.cart?.items;

    if (!Array.isArray(items)) {
      return;
    }

    this.cartQuantities = this.buildCartQuantities(items);
    this.cartQuantityForProduct = this.buildCartQuantityForProduct(items);

    this.updateVariantAvailability();

    const selectedVariant = this.findSelectedVariant();

    if (selectedVariant) {
      this.refreshAddToCartState(selectedVariant);
    }
  }

  // Single-value choices are pre-checked unless sold out. Grouped by radio name, not step,
  // because a combined step (Size + Length) holds two choices.
  autoSelectSingleChoices() {
    const inputsByName = new Map();

    this.querySelectorAll(
      'input[type="radio"][data-product-option-input], input[type="radio"][data-product-property-input]',
    ).forEach((input) => {
      if (!input.name) {
        return;
      }

      if (!inputsByName.has(input.name)) {
        inputsByName.set(input.name, []);
      }

      inputsByName.get(input.name).push(input);
    });

    inputsByName.forEach((inputs) => {
      if (inputs.length !== 1) {
        return;
      }

      const [input] = inputs;

      if (input.checked || input.disabled) {
        return;
      }

      // A sold-out single value stays unselected so the customer isn't dropped onto a sold-out variant.
      if (
        input.matches("[data-product-option-input]") &&
        !this.hasAvailableCombination(
          Number(input.dataset.optionPosition),
          input.dataset.optionValue || input.value,
          this.getSelectedOptions(),
        )
      ) {
        return;
      }

      input.checked = true;

      if (input.matches("[data-product-property-input]")) {
        this.updatePropertyLabel(input);
      }

      if (input.matches("[data-slit-input]")) {
        this.updateSlitProperty(input);
      }
    });
  }

  getSectionData() {
    const script = this.querySelector("[data-section-data]");

    if (!script) {
      return null;
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error("Unable to parse product selector data.", error);

      return null;
    }
  }

  getMtoLookups() {
    const script = this.querySelector("[data-mto-lookups]");

    if (!script) {
      return {};
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error("Unable to parse MTO metaobject lookups.", error);

      return {};
    }
  }

  handleChange(event) {
    this.updateStepSelectionState();

    if (event.target.matches("[data-product-option-input]")) {
      this.handleVariantOptionChange();
      return;
    }

    if (event.target.matches("[data-delivery-time-input]")) {
      this.updateDeliveryEstimate(event.target);
    }

    if (event.target.matches("[data-product-property-input]")) {
      this.updatePropertyLabel(event.target);
    }

    if (event.target.matches("[data-slit-input]")) {
      this.updateSlitProperty(event.target);
    }

    if (event.target.matches("[data-delivery-time-input], [data-product-property-input]")) {
      this.refreshAddToCartState(this.findSelectedVariant());
    }
  }

  handleVariantOptionChange() {
    this.updateSelectedLabels();

    const selectedVariant = this.findSelectedVariant();

    this.updateVariantAvailability();
    this.syncOptionProperties();
    this.updateSlitSwapLinks();

    this.updateOptionSoldOutMessages(selectedVariant);

    if (!selectedVariant) {
      this.setUnavailableState();
      this.updateVariantHelperText(null);

      const colorPreviewVariant = this.findColorPreviewVariant();

      if (colorPreviewVariant) {
        this.dispatchVariantChange(colorPreviewVariant);
      }

      return;
    }

    this.setSelectedVariant(selectedVariant);
  }

  findColorPreviewVariant() {
    const colorInput = Array.from(this.querySelectorAll("[data-product-option-input]:checked")).find(
      (input) => (input.dataset.optionName || "").trim().toLowerCase() === "color",
    );

    if (!colorInput) {
      return null;
    }

    const position = Number(colorInput.dataset.optionPosition);
    const value = colorInput.dataset.optionValue;

    const matches = this.variants.filter((variant) => {
      const option = variant.options.find((candidate) => Number(candidate.position) === position);

      return option && option.name === value;
    });

    if (!matches.length) {
      return null;
    }

    return matches.find((variant) => this.isVariantInStock(variant)) || matches[0];
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll("[data-product-option-input]:checked"))
      .sort(
        (firstInput, secondInput) =>
          Number(firstInput.dataset.optionPosition) - Number(secondInput.dataset.optionPosition),
      )
      .map((input) => ({
        position: Number(input.dataset.optionPosition),
        name: input.dataset.optionValue,
      }));
  }

  updateSlitSwapLinks() {
    const links = this.querySelectorAll("[data-slit-swap-link]");

    if (!links.length) {
      return;
    }

    const selectedOptions = this.getSelectedOptions();

    if (!selectedOptions.length) {
      return;
    }

    links.forEach((link) => {
      let variants;

      try {
        variants = JSON.parse(link.dataset.slitSwapVariants || "[]");
      } catch (error) {
        return;
      }

      const matchedVariant = variants.find((variant) =>
        selectedOptions.every((selectedOption) => {
          const variantOption = (variant.options || []).find(
            (option) => Number(option.position) === selectedOption.position,
          );

          return variantOption && variantOption.name === selectedOption.name;
        }),
      );

      if (!matchedVariant) {
        return;
      }

      let url;

      try {
        url = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }

      url.searchParams.set("variant", matchedVariant.id);
      link.href = `${url.pathname}${url.search}`;
    });
  }

  syncOptionProperties() {
    const container = this.querySelector("[data-option-properties]");

    if (!container) {
      return;
    }

    const seenOptionNames = new Set();

    this.querySelectorAll("[data-product-option-input]:checked").forEach((input) => {
      const optionName = input.dataset.optionName;
      const optionValue = input.dataset.optionValue || "";

      if (!optionName) {
        return;
      }

      const propertyName = optionName.trim().toLowerCase();

      seenOptionNames.add(propertyName);

      const { displayValue, code } = this.resolveOptionProperty(optionName, optionValue);

      this.setOptionPropertyInput(container, propertyName, displayValue);
      this.setOptionPropertyInput(container, `_${propertyName}`, code);
    });

    container.querySelectorAll("[data-option-property]").forEach((propertyInput) => {
      const propertyName = propertyInput.dataset.optionProperty;

      const publicName = propertyName.startsWith("_") ? propertyName.slice(1) : propertyName;

      if (!seenOptionNames.has(publicName)) {
        propertyInput.disabled = true;
      }
    });
  }

  resolveOptionProperty(optionName, optionValue) {
    const normalizedName = optionName.trim().toLowerCase();
    const lookup = this.mtoLookups?.[normalizedName];

    const entry = lookup?.[optionValue];

    if (entry) {
      let displayValue = entry.name;

      if (entry.description) {
        displayValue = `${entry.name}: ${entry.description}`;
      } else if (normalizedName === "mesh") {
        displayValue = `${entry.name}-${entry.code}`;
      }

      return { displayValue, code: entry.code };
    }

    return { displayValue: optionValue, code: optionValue };
  }

  setOptionPropertyInput(container, propertyName, value) {
    let propertyInput = container.querySelector(`[data-option-property="${CSS.escape(propertyName)}"]`);

    if (!propertyInput) {
      propertyInput = document.createElement("input");
      propertyInput.type = "hidden";
      propertyInput.name = `properties[${propertyName}]`;
      propertyInput.dataset.optionProperty = propertyName;
      container.appendChild(propertyInput);
    }

    propertyInput.value = value || "";
    propertyInput.disabled = false;
  }

  findSelectedVariant() {
    if (this.optionCount === 0) {
      return this.variants[0] || null;
    }

    const selectedOptions = this.getSelectedOptions();

    if (!selectedOptions.length || selectedOptions.length < this.optionCount) {
      return null;
    }

    return (
      this.variants.find((variant) =>
        selectedOptions.every((selectedOption) => {
          const variantOption = variant.options.find((option) => Number(option.position) === selectedOption.position);

          return variantOption && variantOption.name === selectedOption.name;
        }),
      ) || null
    );
  }

  getVariantAvailability(variant) {
    const inventoryQuantity = Number(variant.inventoryQuantity);

    const inventoryIsManaged = Boolean(variant.inventoryManagement);
    const inventoryIsTracked = inventoryIsManaged && variant.inventoryPolicy !== "continue";

    if (inventoryIsTracked && inventoryQuantity <= 0) {
      return { inStock: false, reason: "sold-out", remaining: 0 };
    }

    let remaining = null;

    if (inventoryIsTracked) {
      const cartQuantity = this.cartQuantities[String(variant.id)] || 0;

      remaining = Math.max(inventoryQuantity - cartQuantity, 0);
    }

    if (this.isHomeTryOn) {
      const htoRemaining = Math.max(this.htoCartLimit - this.cartQuantityForProduct, 0);

      remaining = remaining === null ? htoRemaining : Math.min(remaining, htoRemaining);

      if (htoRemaining <= 0) {
        return { inStock: false, reason: "hto-limit", remaining: 0 };
      }
    }

    if (remaining !== null && remaining <= 0) {
      return { inStock: false, reason: "max-in-cart", remaining: 0 };
    }

    return { inStock: true, reason: null, remaining };
  }

  isVariantInStock(variant) {
    return this.getVariantAvailability(variant).inStock;
  }

  hasAvailableCombination(optionPosition, optionValue, selectedOptions) {
    const matchingVariants = this.variants.filter((variant) => {
      const candidateOption = variant.options.find((option) => Number(option.position) === optionPosition);

      if (!candidateOption || candidateOption.name !== optionValue) {
        return false;
      }

      return selectedOptions.every((selectedOption) => {
        if (selectedOption.position === optionPosition) {
          return true;
        }

        const variantOption = variant.options.find((option) => Number(option.position) === selectedOption.position);

        return variantOption && variantOption.name === selectedOption.name;
      });
    });

    if (matchingVariants.some((variant) => this.isVariantInStock(variant))) {
      return true;
    }

    // Variant list still loading: keep selectable rather than flash it crossed out.
    return !matchingVariants.length && this.variantsIncomplete;
  }

  setSelectedVariant(variant) {
    if (this.variantInput) {
      this.variantInput.value = variant.id;

      this.variantInput.dispatchEvent(
        new Event("change", {
          bubbles: true,
        }),
      );
    }

    this.setAttribute("current-variant", variant.id);

    const variantIdElement = this.querySelector(".product-form__variant-id");

    if (variantIdElement) {
      variantIdElement.dataset.variantid = variant.id;
    }

    this.updateVariantHelperText(variant);
    this.refreshAddToCartState(variant);
    this.updateVariantUrl(variant);
    this.updateVariantPrice(variant);
    this.dispatchVariantChange(variant);
  }

  setUnavailableState() {
    const productForm = this.closest("product-form");

    const addToCartButton = productForm?.querySelector("[data-product-form-atc]");

    const addToCartContent = addToCartButton?.querySelector(".product-form__atc-content");

    if (addToCartButton) {
      addToCartButton.disabled = true;
    }

    if (addToCartContent) {
      addToCartContent.textContent = productForm?.dataset.localeSelectOptions || "Select options";
    }
  }

  updateVariantHelperText(variant) {
    const helper = this.querySelector("[data-length-helper-text]");

    const helperContent = helper?.querySelector("[data-length-helper-text-content]");

    if (!helper || !helperContent) {
      return;
    }

    const variantNote = typeof variant?.variantNote === "string" ? variant.variantNote.trim() : "";

    helperContent.textContent = variantNote;
    helper.classList.toggle("hidden", !variantNote);
  }

  updateVariantPrice(variant) {
    const priceElement = document.querySelector("[data-main-product-price]");

    if (!priceElement || typeof variant.price !== "number") {
      return;
    }

    if (priceElement.dataset.freePrice === "true") {
      priceElement.dataset.basePrice = 0;
      priceElement.dataset.baseCompareAtPrice = variant.price / 100;
    } else {
      priceElement.dataset.basePrice = variant.price / 100;

      priceElement.dataset.baseCompareAtPrice =
        typeof variant.compareAtPrice === "number" ? variant.compareAtPrice / 100 : 0;
    }

    const selectedDeliveryInput = this.querySelector("[data-delivery-time-input]:checked");

    const surcharge = selectedDeliveryInput
      ? this.parseDeliveryOption(selectedDeliveryInput.dataset.propertyValue || selectedDeliveryInput.value).surcharge
      : null;

    this.applyPriceSurcharge(priceElement, surcharge);
  }

  updateDeliveryEstimate(input) {
    const rawValue = input.dataset.propertyValue || input.value;

    const parsedDelivery = this.parseDeliveryOption(rawValue);
    const dateRange = this.computeShipDateRange(parsedDelivery);

    this.updateShipDate(dateRange);
    this.updateDeliveryPropertyValue(input, parsedDelivery, dateRange);

    const priceElement = document.querySelector("[data-main-product-price]");
    const basePrice = priceElement ? parseFloat(priceElement.dataset.basePrice) : NaN;

    this.updateRushAmountProperty(parsedDelivery.surcharge, basePrice);

    if (priceElement) {
      this.applyPriceSurcharge(priceElement, parsedDelivery.surcharge);
    }
  }

  updateDeliveryPropertyValue(input, parsedDelivery, dateRange) {
    input.value = dateRange ? `${parsedDelivery.title}: Estimated Ship Date: ${dateRange}` : parsedDelivery.title;

    const codeInput = this.querySelector("[data-delivery-code-input]");

    if (codeInput) {
      codeInput.value = parsedDelivery.title;
      codeInput.disabled = !parsedDelivery.title;
    }
  }

  updateSlitProperty(input) {
    const codeInput = this.querySelector("[data-slit-code-input]");

    if (!codeInput) {
      return;
    }

    const entry = this.mtoLookups?.slits?.[input.value];

    codeInput.value = entry ? entry.code : input.value;
    codeInput.disabled = false;
  }

  updateRushAmountProperty(surcharge, basePrice) {
    const rushInput = this.querySelector("[data-rush-amount-input]");

    if (!rushInput) {
      return;
    }

    const amount = this.calculateSurchargeAmount(surcharge, basePrice);

    if (!amount) {
      rushInput.disabled = true;
      rushInput.value = "";
      return;
    }

    const currency = document.querySelector("[data-main-product-price]")?.getAttribute("currency") || "USD";

    rushInput.value = window.formatMoney(amount, false, currency);

    rushInput.disabled = false;
  }

  applyPriceSurcharge(priceElement, surcharge) {
    const basePrice = parseFloat(priceElement.dataset.basePrice);

    if (Number.isNaN(basePrice)) {
      return;
    }

    const surchargeAmount = this.calculateSurchargeAmount(surcharge, basePrice);

    priceElement.setAttribute("price", (basePrice + surchargeAmount).toFixed(2));

    const baseCompareAtPrice = parseFloat(priceElement.dataset.baseCompareAtPrice);

    if (!Number.isNaN(baseCompareAtPrice) && baseCompareAtPrice > 0) {
      priceElement.setAttribute("compare-at-price", (baseCompareAtPrice + surchargeAmount).toFixed(2));
    }
  }

  calculateSurchargeAmount(surcharge, basePrice) {
    if (!surcharge || Number.isNaN(basePrice)) {
      return 0;
    }

    return surcharge.type === "percentage" ? (basePrice * surcharge.value) / 100 : surcharge.value;
  }

  parseDeliveryOption(rawValue) {
    const [title = "", timeframe = "", note = ""] = String(rawValue)
      .split("|")
      .map((part) => part.trim());

    return {
      title,
      timeframe,
      note,
      ...this.parseTimeframe(timeframe),
      surcharge: this.parseSurcharge(note),
    };
  }

  parseTimeframe(timeframe) {
    const match = timeframe.match(/(\d+)\s*(?:-\s*(\d+))?\s*(day|week|month)s?/i);

    if (!match) {
      return {
        minTime: null,
        maxTime: null,
        unit: null,
      };
    }

    const [, min, max, unit] = match;

    return {
      minTime: Number(min),
      maxTime: max ? Number(max) : null,
      unit: `${unit.toLowerCase()}s`,
    };
  }

  parseSurcharge(note) {
    const percentageMatch = note.match(/^\+?\s*([\d,.]+)\s*%$/);

    if (percentageMatch) {
      const percentage = parseFloat(percentageMatch[1].replace(/,/g, ""));
      return Number.isFinite(percentage) && percentage > 0 ? { type: "percentage", value: percentage } : null;
    }

    const fixedMatch = note.match(/^\+?\s*\$\s*([\d,.]+)$/);

    if (fixedMatch) {
      const amount = parseFloat(fixedMatch[1].replace(/,/g, ""));
      return Number.isFinite(amount) && amount > 0 ? { type: "fixed", value: amount } : null;
    }

    return null;
  }

  addTime(date, amount, unit) {
    const result = new Date(date);

    if (unit === "days") {
      result.setDate(result.getDate() + amount);
      return result;
    }

    if (unit === "weeks") {
      result.setDate(result.getDate() + amount * 7);
      return result;
    }

    const day = result.getDate();

    result.setDate(1);
    result.setMonth(result.getMonth() + amount);

    const daysInTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

    result.setDate(Math.min(day, daysInTargetMonth));

    return result;
  }

  formatShipDate(date) {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  computeShipDateRange({ minTime, maxTime, unit }) {
    if (!unit || !minTime) {
      return null;
    }

    const today = new Date();

    const minDate = this.formatShipDate(this.addTime(today, minTime, unit));

    const maxDate = maxTime ? this.formatShipDate(this.addTime(today, maxTime, unit)) : null;

    return maxDate ? `${minDate} - ${maxDate}` : minDate;
  }

  updateShipDate(dateRange) {
    const shipDateElement = this.querySelector("[data-delivery-ship-date]");

    const valueElement = shipDateElement?.querySelector("[data-delivery-ship-date-value]");

    if (!shipDateElement || !valueElement) {
      return;
    }

    if (!dateRange) {
      shipDateElement.hidden = true;
      return;
    }

    valueElement.textContent = dateRange;
    shipDateElement.hidden = false;
  }

  hasUnselectedDeliveryTime() {
    const deliveryInputs = this.querySelectorAll("[data-delivery-time-input]");

    if (!deliveryInputs.length) {
      return false;
    }

    return !this.querySelector("[data-delivery-time-input]:checked");
  }

  refreshAddToCartState(variant) {
    if (!variant || this.hasUnselectedDeliveryTime()) {
      this.setUnavailableState();
      return;
    }

    this.updateAddToCartState(variant);
  }

  updateAddToCartState(variant) {
    const productForm = this.closest("product-form");

    if (!productForm) {
      return;
    }

    const addToCartButton = productForm.querySelector("[data-product-form-atc]");

    const addToCartContent = addToCartButton?.querySelector(".product-form__atc-content");

    const backInStock = productForm.querySelector("[data-product-form-bis]");

    const maxInCartMessage = productForm.querySelector("[data-max-in-cart-message]");

    const availability = this.getVariantAvailability(variant);
    const isInStock = availability.inStock;
    const isMaxInCart = availability.reason === "max-in-cart";
    const isHtoLimit = availability.reason === "hto-limit";

    const showBackInStock = !isInStock && !isMaxInCart && !isHtoLimit;

    if (addToCartButton) {
      addToCartButton.disabled = !isInStock;
      addToCartButton.hidden = false;
      addToCartButton.style.display = showBackInStock ? "none" : "";
    }

    const quantityInput = productForm.querySelector("quantity-input");

    if (quantityInput) {
      quantityInput.style.display = showBackInStock ? "none" : "";
    }

    if (backInStock) {
      if (showBackInStock) {
        backInStock.style.display = "";
        backInStock.setAttribute("selected-variant", variant.id);

        if (variant.url) {
          backInStock.setAttribute("url", variant.url);
        }
      } else {
        backInStock.style.display = "none";
      }
    }

    if (addToCartContent) {
      if (isInStock) {
        const addToCartLabel = productForm.dataset.localeAdd || "Add to cart";

        const currency = document.querySelector("[data-main-product-price]")?.getAttribute("currency") || "USD";

        const displayPrice = addToCartButton?.dataset.freePrice === "true" ? 0 : variant.price;

        // Same delivery surcharge the top price gets in applyPriceSurcharge.
        const selectedDeliveryInput = this.querySelector("[data-delivery-time-input]:checked");

        const surcharge =
          selectedDeliveryInput && displayPrice
            ? this.parseDeliveryOption(selectedDeliveryInput.dataset.propertyValue || selectedDeliveryInput.value)
                .surcharge
            : null;

        const formattedPrice =
          typeof displayPrice === "number"
            ? new Intl.NumberFormat("en-US", {
                style: "currency",
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(displayPrice / 100 + this.calculateSurchargeAmount(surcharge, displayPrice / 100))
            : "";

        addToCartContent.textContent = formattedPrice ? `${addToCartLabel} - ${formattedPrice}` : addToCartLabel;
      } else if (isHtoLimit) {
        addToCartContent.textContent = productForm.dataset.localeHtoLimitButton || "Home Try-On limit reached";
      } else if (isMaxInCart) {
        addToCartContent.textContent = productForm.dataset.localeMaxInCart || "Max quantity in cart";
      } else {
        addToCartContent.textContent = productForm.dataset.localeSoldOut || "Sold out";
      }
    }

    if (maxInCartMessage) {
      if (isHtoLimit) {
        maxInCartMessage.hidden = false;
        maxInCartMessage.textContent = productForm.dataset.localeHtoLimitReached || "";
      } else if (isMaxInCart) {
        maxInCartMessage.hidden = false;
        maxInCartMessage.textContent = productForm.dataset.localeMaxInCart || "Max quantity in cart";
      } else {
        maxInCartMessage.hidden = true;
        maxInCartMessage.textContent = "";
      }
    }

    this.updateQuantityInputMax(productForm, availability);

    this.updateOptionSoldOutMessages(variant);
  }

  updateOptionSoldOutMessages(variant) {
    const isInStock = variant ? this.getVariantAvailability(variant).inStock : true;

    this.querySelectorAll("[data-option-sold-out-message]").forEach((message) => {
      message.classList.toggle("hidden", isInStock);
    });
  }

  updateQuantityInputMax(productForm, availability) {
    const quantityInputElement = productForm.querySelector("quantity-input");

    const select = quantityInputElement?.querySelector(".quantity__select");

    if (!quantityInputElement || !select) {
      return;
    }

    select.disabled = !availability.inStock;

    if (availability.remaining === null || availability.remaining === undefined) {
      return;
    }

    const dropdownMax = Math.min(Math.max(availability.remaining, 1), 10);

    const existingOptionCount = select.options.length;

    if (existingOptionCount === dropdownMax) {
      return;
    }

    const currentValue = Number(select.value) || 1;
    const nextValue = Math.min(currentValue, dropdownMax);

    select.innerHTML = "";

    for (let quantity = 1; quantity <= dropdownMax; quantity += 1) {
      const option = document.createElement("option");
      option.value = String(quantity);
      option.textContent = String(quantity);
      option.selected = quantity === nextValue;
      select.appendChild(option);
    }
  }

  updateVariantUrl(variant) {
    if (!variant.url) {
      return;
    }

    const url = new URL(variant.url, window.location.origin);

    window.history.replaceState(
      {
        variantId: variant.id,
      },
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }

  updateSelectedLabels() {
    this.querySelectorAll("[data-option-placeholder]").forEach((label) => {
      const position = label.dataset.optionSelectedValue;

      const hasChecked = this.querySelector(`[data-product-option-input][data-option-position="${position}"]:checked`);

      if (!hasChecked) {
        label.textContent = label.dataset.optionPlaceholder;
      }
    });

    this.querySelectorAll("[data-product-option-input]:checked").forEach((input) => {
      const position = input.dataset.optionPosition;
      const value = input.dataset.optionValue || "";

      this.querySelectorAll(`[data-option-selected-value="${position}"]`).forEach((label) => {
        label.textContent = value;
      });
    });
  }

  updateStepSelectionState() {
    this.querySelectorAll("[data-product-form-step]").forEach((step) => {
      if (step.dataset.stepType === "product-group") {
        return;
      }

      if (step.dataset.stepType === "swatch-fabric") {
        step.classList.remove("product-form__accordion--unselected");
        return;
      }

      const hasSelection =
        step.dataset.stepType === "variant-fabric"
          ? step.querySelector(".product-fabric-option--selected")
          : step.querySelector("[data-product-option-input]:checked, [data-product-property-input]:checked");

      step.classList.toggle("product-form__accordion--unselected", !hasSelection);

      const secondPart = step.querySelector("[data-size-length-second-part]");

      if (secondPart) {
        secondPart.hidden = !hasSelection;
      }

      if (!hasSelection) {
        step.querySelectorAll("[data-option-placeholder]").forEach((label) => {
          label.textContent = label.dataset.optionPlaceholder;
        });
      }
    });
  }

  updatePropertyLabel(input) {
    const step = input.closest("[data-product-form-step]");

    const label = step?.querySelector("[data-property-selected-value]");

    if (!label) {
      return;
    }

    const rawValue = input.dataset.propertyValue || input.value || "";

    if (!rawValue.includes("|")) {
      label.textContent = rawValue;
      return;
    }

    const [title, , note] = rawValue.split("|").map((part) => part.trim());

    label.textContent = note === "Included" ? `${title} (${note})` : title;
  }

  updateVariantAvailability() {
    const selectedOptions = this.getSelectedOptions();

    this.querySelectorAll("[data-product-option-input]").forEach((input) => {
      const optionPosition = Number(input.dataset.optionPosition);

      const optionValue = input.dataset.optionValue || input.value;

      const isAvailable = this.hasAvailableCombination(optionPosition, optionValue, selectedOptions);

      const label = input.closest(".product-form__option-label, .product-swatch");

      const isSwatchLabel = label?.classList.contains("product-swatch");

      const standardValue = label?.querySelector(".product-form__option-value");

      input.classList.toggle("product-form__option-input--unavailable", !isAvailable);

      input.setAttribute("aria-disabled", String(!isAvailable));

      label?.classList.toggle(
        isSwatchLabel ? "product-swatch--sold-out" : "product-form__option-label--sold-out",
        !isAvailable,
      );

      standardValue?.classList.toggle("product-form__option-value--sold-out", !isAvailable);
    });
  }

  dispatchVariantChange(variant) {
    this.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          variant,
          variantId: variant.id,
        },
      }),
    );

    document.dispatchEvent(
      new CustomEvent("product:variant-change", {
        detail: {
          variant,
          variantId: variant.id,
          productForm: this,
        },
      }),
    );
  }
}

if (!customElements.get("product-form-selectors")) {
  customElements.define("product-form-selectors", DynamicProductFormSelectors);
}
